import {
  TRESPONSE_HL_BOARD_LISTING,
  TRESPONSE_HL_BOARD_SECTION_WISE_LISTING,
  THLEntityGroupData,
  THLEntityData,
  THLElementData,
  TDate,
  HLBoardFactoryDTO,
  TBoardViewMode,
} from '@monorepo/types';
import { TIMELINE_MODEL } from '../timeline.model';
import { TEntityActions, TRange, TStripElement, TTimelineModel } from '../timeline.types';
import { getLeftNavBackgroundColour } from '../timeline.utils';

function _capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function createFactoryEntity(
  factoryInfo: HLBoardFactoryDTO,
  boardViewType: TBoardViewMode,
  isExpanded: boolean,
): THLTimelineModel['entities'][number]['entities'][number] {
  return {
    id: `entity_${factoryInfo.factoryId}`,
    data: { type: 'FACTORY' as const, factoryData: factoryInfo },
    name: {
      displayTxt: isExpanded ? 'Collapse ▲' : 'Expand ▼',
      isClickable: true,
    },
    metadata: {
      title: '',
      subTitle: factoryInfo.factoryName,
      leftCount: 0,
      rightCount: 0,
    },
    elements: factoryInfo.capacityStrips.map((c, stripIndex): TStripElement<THLElementData> => {
      const allocatedCapacityDetails = `(${Object.entries(c.allocatedCapacityInfo.capacityByOrderType)
        .map(([key, value]) => `${_capitalize(key.toLowerCase())}: ${value}`)
        .join(', ')})`;

      const balanceCapacity = `Balance: ${(c.installedCapacity - c.allocatedCapacityInfo.totalCapacity).toFixed(2)}`;
      const installedAdditionalInfo = `Installed Capacity: ${c.installedCapacity} ${balanceCapacity} ${allocatedCapacityDetails}`;
      const startDate = `${c.dateRange.from} 02:00:00`;
      const endDate = `${c.dateRange.to} 22:00:00`;

      return {
        type: 'STRIP' as const,
        range: { startDate, endDate } as TRange,
        stackCount: 0 as const,
        data: { type: 'FACTORY_STRIP' as const, elementData: c },
        iconList: [],
        id: `factory_strip_${factoryInfo.factoryId}_${stripIndex}`,
        mask: false,
        name: `Allocated Capacity: ${c.allocatedCapacityInfo.totalCapacity}`,
        additionalInfo: installedAdditionalInfo,
        color: c.stripColors.allocated,
        textList: [
          {
            value: boardViewType === 'MONTHLY' ? '' : `${c.installedCapacity}/${c.allocatedCapacityInfo.totalCapacity}`,
            color: '#000',
            align: 'top',
            justify: 'center',
          },
          {
            value: c.allocatedCapacityPercentage ? `${c.allocatedCapacityPercentage} %` : '- -',
            color: '#000',
            align: 'center',
            justify: 'center',
          },
        ],
      };
    }),
  };
}

function createSectionEntities(sectionData: TRESPONSE_HL_BOARD_SECTION_WISE_LISTING, factoryId: string) {
  return sectionData.sectionCapacities.map<THLTimelineModel['entities'][number]['entities'][number]>(
    (section, sectionIndex) => ({
      id: `entity_${factoryId}_${section.sectionDisplayName}_${sectionIndex}`,
      data: { type: 'SECTION' as const, sectionDisplayName: section.sectionDisplayName, factoryId },
      name: { displayTxt: '' },
      metadata: { subTitle: section.sectionDisplayName, leftCount: 0, rightCount: 0, title: '' },
      elements: section.capacityStrips.map((strip, stripIndex): TStripElement<THLElementData> => {
        const startDate = `${strip.dateRange.from} 02:00:00`;
        const endDate = `${strip.dateRange.to} 22:00:00`;
        const installedAdditionalInfo = `Installed Capacity: ${strip.installedCapacity}`;

        return {
          type: 'STRIP' as const,
          id: `section_strip_${factoryId}_${section.sectionDisplayName}_${sectionIndex}_${stripIndex}`,
          name: `Allocated Capacity: ${strip.allocatedCapacity}`,
          additionalInfo: installedAdditionalInfo,
          range: { startDate, endDate } as TRange,
          stackCount: 0 as const,
          data: { type: 'SECTION_STRIP' as const, elementData: strip },
          iconList: [],
          mask: false,
          color: strip.stripColors.allocated,
          textList: [
            {
              value: `${strip.installedCapacity}/${strip.allocatedCapacity}`,
              color: '#000',
              align: 'center',
              justify: 'center',
            },
          ],
        };
      }),
    }),
  );
}

export const HL_MODEL = {
  createModel: (
    hlBoardResponse: TRESPONSE_HL_BOARD_LISTING,
    expandedSections?: Map<string, TRESPONSE_HL_BOARD_SECTION_WISE_LISTING | null>,
  ): THLTimelineModel => {
    const flattenFactory = hlBoardResponse.hlBoardDTO.clusterDetails.map((cluster) => cluster.factoryDetails).flat();

    return TIMELINE_MODEL.createTimelineModel<THLEntityGroupData, THLEntityData, THLElementData>(
      hlBoardResponse.boardResponseView,
      flattenFactory,
      (factoryInfo, factoryIndex) => {
        const expandedSectionsData = expandedSections?.get(factoryInfo.factoryId);

        const factoryEntity = createFactoryEntity(
          factoryInfo,
          hlBoardResponse.boardResponseView.boardViewType,
          !!expandedSectionsData,
        );

        const sectionEntities = expandedSectionsData
          ? createSectionEntities(expandedSectionsData, factoryInfo.factoryId)
          : [];

        return {
          id: `entity_group_${factoryInfo.factoryId}`,
          name: factoryInfo.factoryId,
          entities: [factoryEntity, ...sectionEntities],
          data: factoryInfo,
          holidays: [...factoryInfo.factoryMetadata.holidays, ...factoryInfo.factoryMetadata.nonWorkingDays] as TDate[],
          backgroundColor: getLeftNavBackgroundColour(factoryIndex, '#cfd0d4', '#f8f9f9'),
        };
      },
    );
  },

  changeView: (type: TTimelineModel['boardView']['boardViewType'], timelineModel: TTimelineModel) => {
    return TIMELINE_MODEL.changeTimelineType(type, timelineModel);
  },
};

export type THLTimelineModel = TTimelineModel<HLBoardFactoryDTO, THLEntityData, THLElementData>;
export type THLEntityAction = TEntityActions<THLEntityGroupData, THLEntityData, THLElementData>;
